/*
 OK FBI — Public page-content bindings.

 A single hook that fetches the OWNER-approved website content (or the draft
 when `?preview=1` is present and the visitor is an OWNER). Public section
 components consume this via lightweight lookups so they can render whatever
 field-value the OWNER edited from `/host/website-editor`, or fall back to
 the hard-coded design copy when a field is empty/undefined.

 Slice A covers `home` and `plans`. Slice B/C extend by adding more page keys.
*/
import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

/** Returns { pages, loaded, isPreview } — pages is the same shape the
 *  backend serves under /api/page-content/published (or /draft when
 *  `?preview=1` is set on the URL). */
export function usePageContent() {
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get("preview") === "1";
  const [pages, setPages] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const url = isPreview ? "/page-content/draft" : "/page-content/published";
      const { data } = await api.get(url);
      setPages((data && data.pages) || {});
    } catch {
      // Fall back silently — public pages have hard-coded defaults.
      setPages({});
    } finally {
      setLoaded(true);
    }
  }, [isPreview]);

  useEffect(() => { load(); }, [load]);

  return { pages, loaded, isPreview, reload: load };
}

/** Given the shared `pages` object, returns an array of section objects
 *  for a specific page-key (already filtered by visible). Sections stay in
 *  the OWNER-defined order. */
export function useSectionsFor(pages, pageKey) {
  if (!pages || !pages[pageKey]) return [];
  const sections = (pages[pageKey].sections || []).filter((s) => s.visible !== false);
  return sections;
}

/** Given a section, returns { fields, items } already filtered for visible
 *  items. Kept tiny so section components can `const { fields, items } = useSection(sec)`. */
export function readSection(sec) {
  if (!sec) return { fields: {}, items: [] };
  return {
    fields: sec.fields || {},
    items: (sec.items || []).filter((i) => i.visible !== false),
  };
}

/** Safe getter that returns `fallback` when the OWNER-provided field is
 *  empty/undefined. Prevents empty-string overrides from wiping design copy. */
export function pick(value, fallback) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  return value;
}
